import { defineStore } from "pinia";

import { captureMessage } from "@/error/sentry";

const items = [
  "checkAuth",
  "login",
  "user",
  "featureToggles",
  "connect",
  "initBackend",
  "setSession",
  "linkTypes",
  "links",
  "almTypes",
  "stickerTypes",
  "stickyTypes",
  "session",
  "boards",
] as const;

type LoadingItem = (typeof items)[number];

export const useLoadingStore = defineStore("loading", {
  state: () => ({
    items: { started: new Array<LoadingItem>(), loading: new Array<LoadingItem>() },
    started: 0,
    finished: 0,
    ended: false,
  }),
  getters: {
    progress: (state) => ({
      started: state.started / items.length,
      finished: state.finished / items.length,
    }),
    count: (state) => (type: LoadingItem) =>
      state.items.started.filter((item) => item === type).length,
  },
  actions: {
    start() {
      this.items.started = [];
      this.items.loading = [];
      this.started = 0;
      this.finished = 0;
    },
    end() {
      this.ended = true;
      if (this.started !== items.length || this.finished !== items.length) {
        // TODO avoid sentry spam, remove when REN-13052 is fixed
        if (this.multipleSetSession()) {
          return;
        }
        captureMessage("loading items count not reached", {
          counts: {
            total: items.length,
            items: this.items,
            started: this.started,
            finished: this.finished,
          },
        });
      }
    },
    multipleSetSession() {
      const setSessionCount = this.count("setSession");
      return (
        this.started === this.finished &&
        setSessionCount <= 3 &&
        setSessionCount - 1 === this.started - items.length
      );
    },
    load<T>(item: LoadingItem, action: Promise<T>): Promise<T> {
      if (this.ended) {
        return action;
      }
      this.started++;
      this.items.started.push(item);
      this.items.loading.push(item);
      return action.finally(() => {
        this.finished++;
        this.items.loading.splice(
          this.items.loading.findIndex((i) => i === item),
          1,
        );
      });
    },
  },
});
